'use client';

import { Breakpoint } from '@/styles/breakpoints';
import { ReactNode, useCallback, useEffect, useState } from 'react';

const sizes: readonly Breakpoint[] = ['xs', 'sm', 'md', 'lg', 'xl'] as const;

function getNextLarger(
  size: Breakpoint,
  elements: { [K in Breakpoint]?: ReactNode },
  defaultElement: ReactNode = <div></div>
) {
  const startIndex = sizes.indexOf(size);
  for (let i = startIndex; i < sizes.length; i++) {
    const el = elements[sizes[i]];
    if (el) return el;
  }
  return defaultElement;
}

function getNextSmaller(
  size: Breakpoint,
  elements: { [K in Breakpoint]?: ReactNode },
  defaultElement: ReactNode = <div></div>
) {
  const startIndex = sizes.indexOf(size);
  for (let i = startIndex; i >= 0; i--) {
    const el = elements[sizes[i]];
    if (el) return el;
  }
  return defaultElement;
}

function cssVar(name: string) {
  const styles = window.getComputedStyle(document.documentElement);
  return styles.getPropertyValue(name);
}

function and(a: string, b: string) {
  return `${a} and ${b}`;
}

export type ResponsiveProps = {
  xs?: ReactNode;
  sm?: ReactNode;
  md?: ReactNode;
  lg?: ReactNode;
  xl?: ReactNode;
  fallback?: Breakpoint;
};

const Responsive: React.FC<ResponsiveProps> = ({ fallback = 'xs', ...elements }) => {
  const [currentSize, setCurrentSize] = useState<Breakpoint | null>(null);
  const [breaks, setBreaks] = useState<{ [K in Breakpoint]: any } | null>(null);

  const defaultElement = Object.values(elements).find((x) => Boolean(x));
  const elementForSize = (size: Breakpoint | null) =>
    getNextSmaller(
      size || fallback,
      elements,
      getNextLarger(size || fallback, elements, defaultElement)
    );

  const onResize = useCallback(
    function (this: Window, e: UIEvent) {
      if (!breaks) return;

      // Helper functions to construct media queries
      function minWidth(size: Breakpoint) {
        return `(min-width: ${breaks?.[size]})`;
      }

      function maxWidth(size: Breakpoint) {
        return `(max-width: ${breaks?.[size]})`;
      }

      // Only update value if it actually changed to avoid re-renders
      function update(size: Breakpoint) {
        if (size !== currentSize) {
          setCurrentSize(size);
        }
      }

      if (this.matchMedia(maxWidth('sm')).matches) {
        update('xs');
      } else if (this.matchMedia(and(minWidth('sm'), maxWidth('md'))).matches) {
        update('sm');
      } else if (this.matchMedia(and(minWidth('md'), maxWidth('lg'))).matches) {
        update('md');
      } else if (this.matchMedia(and(minWidth('lg'), maxWidth('xl'))).matches) {
        update('lg');
      } else {
        update('xl');
      }
    },
    [currentSize, breaks, setCurrentSize]
  );

  // Get all breakpoints
  useEffect(() => {
    setBreaks(Object.fromEntries(sizes.map((size) => [size, cssVar(`--break-${size}`)])) as any);
  }, [setBreaks]);

  useEffect(() => {
    window.addEventListener('resize', onResize);

    // Run once now to get current size
    window.dispatchEvent(new UIEvent('resize'));

    return () => {
      window.removeEventListener('resize', onResize);
    };
  });

  // Rendering
  return elementForSize(currentSize);
};

export default Responsive;
